import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { auth, db, storage } from '../services/firebase/config';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { logoutUser } from '../services/firebase/auth';
import { updateUserProfile } from '../services/firebase/updateProfile';
import '../styles/global.css';
import '../styles/profilePage.css';

// دالة آمنة لقياس المسافة الجغرافية
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  try {
    // التحقق من صحة الإحداثيات
    if (typeof lat1 !== 'number' || typeof lon1 !== 'number' || 
        typeof lat2 !== 'number' || typeof lon2 !== 'number') {
      return Infinity;
    }
    
    // صيغة هافرساين لحساب المسافة
    const toRad = deg => (deg * Math.PI) / 180;
    const R = 6371; // نصف قطر الأرض بالكيلومتر
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  } catch (error) {
    console.error("Error calculating distance:", error);
    return Infinity;
  }
};

// أيقونة الإعدادات كمكون منفصل - معبأة باللون الأسود
const SettingsIcon = ({ onClick }) => (
  <svg
    className="settings-icon"
    onClick={onClick}
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="black"
    stroke="none"
  >
    <path d="M12,15.5A3.5,3.5 0 0,1 8.5,12A3.5,3.5 0 0,1 12,8.5A3.5,3.5 0 0,1 15.5,12A3.5,3.5 0 0,1 12,15.5M19.43,12.97C19.47,12.65 19.5,12.33 19.5,12C19.5,11.67 19.47,11.34 19.43,11L21.54,9.37C21.73,9.22 21.78,8.95 21.66,8.73L19.66,5.27C19.54,5.05 19.27,4.96 19.05,5.05L16.56,6.05C16.04,5.66 15.5,5.32 14.87,5.07L14.5,2.42C14.46,2.18 14.25,2 14,2H10C9.75,2 9.54,2.18 9.5,2.42L9.13,5.07C8.5,5.32 7.96,5.66 7.44,6.05L4.95,5.05C4.73,4.96 4.46,5.05 4.34,5.27L2.34,8.73C2.21,8.95 2.27,9.22 2.46,9.37L4.57,11C4.53,11.34 4.5,11.67 4.5,12C4.5,12.33 4.53,12.65 4.57,12.97L2.46,14.63C2.27,14.78 2.21,15.05 2.34,15.27L4.34,18.73C4.46,18.95 4.73,19.03 4.95,18.95L7.44,17.94C7.96,18.34 8.5,18.68 9.13,18.93L9.5,21.58C9.54,21.82 9.75,22 10,22H14C14.25,22 14.46,21.82 14.5,21.58L14.87,18.93C15.5,18.67 16.04,18.34 16.56,17.94L19.05,18.95C19.27,19.03 19.54,18.95 19.66,18.73L21.66,15.27C21.78,15.05 21.73,14.78 21.54,14.63L19.43,12.97Z" />
  </svg>
);

// أيقونة تسجيل الخروج كمكون منفصل
const LogoutIcon = ({ onClick }) => (
  <img
    src="/images/logout.png"
    alt="Logout"
    className="logout-icon"
    onClick={onClick}
    title="Logout"
  />
);

// مكون نافذة تأكيد تسجيل الخروج
const LogoutConfirmationModal = ({ onClose, onConfirm }) => {
  return (
    <div className="logout-modal" onClick={onClose}>
      <div className="logout-modal-content" onClick={e => e.stopPropagation()}>
        <h3>Are you sure you want to logout?</h3>
        <div className="logout-buttons">
          <button className="logout-button-no" onClick={onClose}>No</button>
          <button className="logout-button-yes" onClick={onConfirm}>Yes</button>
        </div>
      </div>
    </div>
  );
};

// مكون أيقونات التواصل الاجتماعي
const SocialIcons = ({ social }) => {
  const socialData = social || {};
  const validIcons = [];
  
  if (socialData.email) {
    validIcons.push({ key: 'email', label: '✉️', url: `mailto:${socialData.email}` });
  }
  
  if (socialData.facebook) {
    validIcons.push({ key: 'facebook', label: 'f', url: socialData.facebook });
  }
  
  if (socialData.instagram) {
    validIcons.push({ key: 'instagram', label: 'I', url: socialData.instagram });
  }
  
  if (socialData.youtube) {
    validIcons.push({ key: 'youtube', label: '▶', url: socialData.youtube });
  }
  
  if (socialData.x) {
    validIcons.push({ key: 'x', label: 'X', url: socialData.x });
  }

  return (
    <div className="social-icons">
      {validIcons.map(icon => (
        <a 
          key={icon.key} 
          href={icon.url} 
          className="social-icon" 
          target="_blank"
          rel="noopener noreferrer"
        >
          {icon.key === 'email' ? (
            <img src="/images/email.png" alt="Email" className="social-icon-img" />
          ) : icon.key === 'facebook' ? (
            <img src="/images/facebook.png" alt="Facebook" className="social-icon-img" />
          ) : icon.key === 'instagram' ? (
            <img src="/images/instagram.png" alt="Instagram" className="social-icon-img" />
          ) : icon.key === 'youtube' ? (
            <img src="/images/youtube.png" alt="YouTube" className="social-icon-img" />
          ) : icon.key === 'x' ? (
            <img src="/images/X.png" alt="X" className="social-icon-img" />
          ) : (
            icon.label
          )}
        </a>
      ))}
    </div>
  );
};

// مكون نافذة الإعدادات
const SettingsModal = ({ author, onClose, onSave }) => {
  const defaultData = {
    displayName: '',
    columnTitle: '',
    photoURL: '',
    bio: '',
    social: {
      email: '',
      facebook: '',
      instagram: '',
      youtube: '',
      x: ''
    }
  };
  
  const safeAuthor = author || defaultData;
  const safeSocial = safeAuthor.social || {};
  
  const [formData, setFormData] = useState({
    displayName: safeAuthor.displayName || '',
    columnTitle: safeAuthor.columnTitle || '',
    photoURL: safeAuthor.photoURL || '',
    bio: safeAuthor.bio || '',
    social: {
      email: safeSocial.email || '',
      facebook: safeSocial.facebook || '',
      instagram: safeSocial.instagram || '',
      youtube: safeSocial.youtube || '',
      x: safeSocial.x || ''
    }
  });
  
  const [imageFile, setImageFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(safeAuthor.photoURL || '');

  const handleChange = (e) => {
    if (!e || !e.target || typeof e.target.name !== 'string') return;
    
    const { name, value } = e.target;
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // التحقق من نوع الملف
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setUploadStatus('Please select a valid image file (JPEG, PNG, GIF, WEBP)');
      return;
    }
    
    // التحقق من حجم الملف (الحد الأقصى 2 ميجابايت)
    const maxSize = 2 * 1024 * 1024; // 2MB
    if (file.size > maxSize) {
      setUploadStatus('Image is too large. Please select an image less than 2MB');
      return;
    }
    
    setImageFile(file);
    setUploadStatus(null);
    
    // إنشاء معاينة للصورة
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result);
    };
    reader.readAsDataURL(file);
  };
  
  // وظيفة رفع الصورة إلى Firebase Storage
  const uploadImage = async () => {
    if (!imageFile) return null;
    
    try {
      setIsUploading(true);
      setUploadStatus('Uploading image...');
      
      // الحصول على معرف المستخدم الحالي بأمان
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('No authenticated user found');
      }
      
      // إنشاء مسار فريد للصورة - استخدام المسار المسموح به في قواعد Firebase
      const timestamp = new Date().getTime();
      const fileName = `profile_images/${currentUser.uid}/${timestamp}_profile.jpg`;
      const storageRef = ref(storage, fileName);
      
      console.log('Uploading image to:', fileName);
      
      // رفع الملف
      const snapshot = await uploadBytes(storageRef, imageFile);
      console.log('Image uploaded successfully:', snapshot);
      
      // الحصول على رابط التنزيل
      const downloadURL = await getDownloadURL(storageRef);
      console.log('Download URL obtained:', downloadURL);
      
      setUploadStatus('Image uploaded successfully');
      setIsUploading(false);
      
      return downloadURL;
    } catch (error) {
      console.error('Error uploading image:', error);
      console.error('Error details:', error.code, error.message);
      
      // رسائل خطأ أكثر تفصيلاً
      if (error.code === 'storage/unauthorized') {
        setUploadStatus('Permission denied. Please contact the administrator.');
      } else if (error.code === 'storage/canceled') {
        setUploadStatus('Upload was cancelled.');
      } else if (error.code === 'storage/retry-limit-exceeded') {
        setUploadStatus('Upload failed due to network issues. Please try again.');
      } else {
        setUploadStatus(`Failed to upload image: ${error.message}`);
      }
      
      setIsUploading(false);
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // قبل رفع الصورة، تحقق من أن المستخدم مسجل دخوله
      if (!auth.currentUser) {
        setUploadStatus('You must be logged in to update your profile.');
        return;
      }
      
      // إذا كان هناك صورة جديدة، قم برفعها أولاً
      let updatedFormData = { ...formData };
      
      if (imageFile) {
        setUploadStatus('Preparing to upload image...');
        const imageUrl = await uploadImage();
        if (imageUrl) {
          updatedFormData.photoURL = imageUrl;
          setUploadStatus('Image uploaded successfully, updating profile...');
        } else {
          if (!formData.photoURL) {
            // إذا فشل الرفع ولم يكن هناك صورة سابقة، نوقف العملية ونعرض رسالة خطأ
            setUploadStatus('Image upload failed. Please try again before saving.');
            return;
          } else {
            setUploadStatus('Image upload failed, but proceeding with other updates.');
          }
        }
      }
      
      // حفظ البيانات المحدثة
      await onSave(updatedFormData);
      setUploadStatus('Profile updated successfully');
      
      // تحديث معاينة الصورة
      setPreviewUrl(updatedFormData.photoURL);
    } catch (error) {
      console.error('Error saving profile:', error);
      setUploadStatus(`Failed to save profile: ${error.message || 'Unknown error'}`);
    }
  };

  return (
    <div className="settings-modal" onClick={onClose}>
      <div className="settings-modal-content" onClick={e => e.stopPropagation()}>
        <h2 className="settings-title">Edit Profile</h2>
        <form onSubmit={handleSubmit}>
          <div className="settings-form-group">
            <label className="settings-label">Profile Picture</label>
            <div className="profile-image-upload">
              <img 
                src={previewUrl || '/images/default-user.png'} 
                alt={formData.displayName || 'Profile'} 
                className="profile-preview-image" 
              />
              <div className="image-upload-controls">
                <input 
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="file-input"
                  id="profile-image-upload"
                />
                <label htmlFor="profile-image-upload" className="file-input-label">
                  Choose Image
                </label>
                {uploadStatus && (
                  <div className={`upload-status ${isUploading ? 'uploading' : ''}`}>
                    {uploadStatus}
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="settings-form-group">
            <label className="settings-label">Author Name</label>
            <input 
              type="text" 
              name="displayName"
              className="settings-input" 
              placeholder="Enter your name"
              value={formData.displayName}
              onChange={handleChange}
              required
            />
          </div>
          
          <div className="settings-form-group">
            <label className="settings-label">Column Title</label>
            <input 
              type="text" 
              name="columnTitle"
              className="settings-input" 
              placeholder="Enter your column title"
              value={formData.columnTitle}
              onChange={handleChange}
              required
            />
          </div>
          
          <div className="settings-form-group">
            <label className="settings-label">Bio</label>
            <textarea 
              name="bio"
              className="settings-textarea" 
              placeholder="Enter a short bio about yourself"
              value={formData.bio}
              onChange={handleChange}
              rows="4"
            ></textarea>
          </div>
          
          <div className="settings-form-group">
            <label className="settings-label">Social Media</label>
            <div className="social-inputs">
              <div className="social-input-group">
                <label>Email</label>
                <input 
                  type="email" 
                  name="social.email"
                  className="settings-input" 
                  placeholder="Email address"
                  value={formData.social.email}
                  onChange={handleChange}
                />
              </div>
              <div className="social-input-group">
                <label>Facebook</label>
                <input 
                  type="text" 
                  name="social.facebook"
                  className="settings-input" 
                  placeholder="Facebook URL"
                  value={formData.social.facebook}
                  onChange={handleChange}
                />
              </div>
              <div className="social-input-group">
                <label>Instagram</label>
                <input 
                  type="text" 
                  name="social.instagram"
                  className="settings-input" 
                  placeholder="Instagram URL"
                  value={formData.social.instagram}
                  onChange={handleChange}
                />
              </div>
              <div className="social-input-group">
                <label>YouTube</label>
                <input 
                  type="text" 
                  name="social.youtube"
                  className="settings-input" 
                  placeholder="YouTube URL"
                  value={formData.social.youtube}
                  onChange={handleChange}
                />
              </div>
              <div className="social-input-group">
                <label>X (Twitter)</label>
                <input 
                  type="text" 
                  name="social.x"
                  className="settings-input" 
                  placeholder="X (Twitter) URL"
                  value={formData.social.x}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>
          
          <div className="settings-form-actions">
            <button 
              type="submit" 
              className="settings-button"
              disabled={isUploading}
            >
              {isUploading ? 'Saving...' : 'Save Changes'}
            </button>
            <button 
              type="button" 
              className="settings-button cancel" 
              onClick={onClose}
              disabled={isUploading}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ProfilePage = () => {
  // استخراج المعرف من المسار
  const { id } = useParams();
  const [author, setAuthor] = useState(null);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCurrentUser, setIsCurrentUser] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [isInRange, setIsInRange] = useState(true); // نفترض أنه في النطاق افتراضيًا
  const auth = getAuth();
  const navigate = useNavigate();
  const [authChecked, setAuthChecked] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  
  // التحقق من تسجيل دخول المستخدم
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      console.log("Auth state changed:", user ? `User logged in: ${user.uid}` : "No user logged in");
      setAuthChecked(true);
    });
    
    return () => unsubscribe();
  }, [auth]);
  
  // استخدام معرف المستخدم الحالي إذا لم يتم تحديد معرف في المسار
  const targetUserId = id || (auth.currentUser ? auth.currentUser.uid : null);
  
  // طباعة معلومات عن المستخدم الحالي
  useEffect(() => {
    if (auth.currentUser) {
      console.log("Current authenticated user:", auth.currentUser.uid);
      console.log("Email verification status:", auth.currentUser.emailVerified);
    } else {
      console.log("No user is authenticated");
    }
  }, [auth.currentUser]);

  // الحصول على موقع المستخدم
  useEffect(() => {
    if (!navigator.geolocation) return;
    
    const geo = navigator.geolocation;
    const geoOptions = { 
      enableHighAccuracy: true, 
      timeout: 5000, 
      maximumAge: 0 
    };
    
    let geoWatchId = null;
    
    try {
      geoWatchId = geo.watchPosition(
        (position) => {
          if (position && position.coords) {
            setUserLocation({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude
            });
          }
        },
        (error) => {
          console.log("Geolocation error:", error.message);
          setUserLocation(null);
        },
        geoOptions
      );
    } catch (e) {
      console.error("Error setting up geolocation:", e);
    }
    
    // تنظيف مراقبة الموقع عند إزالة المكون
    return () => {
      if (geoWatchId !== null) {
        geo.clearWatch(geoWatchId);
      }
    };
  }, []);

  // التحقق من المستخدم الحالي
  useEffect(() => {
    if (!targetUserId) {
      setLoading(false);
      return;
    }
    
    const checkCurrentUser = async () => {
      const unsubscribe = onAuthStateChanged(auth, (user) => {
        setIsCurrentUser(user && user.uid === targetUserId);
      });
      
      return unsubscribe;
    };
    
    const unsubscribe = checkCurrentUser();
    return () => unsubscribe.then(fn => fn());
  }, [auth, targetUserId]);

  // جلب بيانات المستخدم المستهدف
  useEffect(() => {
    if (!targetUserId) {
      setLoading(false);
      setAuthor(null);
      return;
    }
    
    const fetchUserData = async () => {
      try {
        // جلب بيانات المستخدم
        const userRef = doc(db, 'users', targetUserId);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists()) {
          const userData = userSnap.data();
          console.log("User data loaded:", targetUserId);
          
          // التأكد من وجود الحقول الضرورية
          if (!userData.social) userData.social = {};
          
          setAuthor({...userData, id: targetUserId});
          
          // التحقق من إمكانية الوصول استنادًا إلى الموقع
          // تخطي هذا التحقق إذا كان المستخدم هو نفسه صاحب الصفحة
          if (isCurrentUser) {
            setIsInRange(true);
          } else if (userLocation && userData.location) {
            // حساب المسافة بين المستخدم الحالي والكاتب
            const distance = calculateDistance(
              userLocation.latitude,
              userLocation.longitude,
              Number(userData.location.latitude),
              Number(userData.location.longitude)
            );
            
            const maxDistance = 100; // كيلومتر
            setIsInRange(distance <= maxDistance);
          } else {
            // إذا لم يكن هناك بيانات موقع، نسمح بالوصول
            setIsInRange(true);
          }
          
          // جلب مقالات المستخدم
          try {
            const articlesQuery = query(
              collection(db, 'articles'),
              where('authorId', '==', targetUserId)
            );
            
            const articlesSnapshot = await getDocs(articlesQuery);
            if (!articlesSnapshot.empty) {
              const articlesList = articlesSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
              }));
              setArticles(articlesList);
            } else {
              setArticles([]);
            }
          } catch (error) {
            console.error("Error fetching articles:", error);
            setArticles([]);
          }
        } else {
          console.log("User not found:", targetUserId);
          setAuthor(null);
        }
      } catch (error) {
        console.error("Error in fetchUserData:", error);
        setAuthor(null);
      } finally {
        setLoading(false);
      }
    };
    
    fetchUserData();
  }, [targetUserId, userLocation, isCurrentUser]);

  const handleSettingsToggle = () => {
    setShowSettingsModal(!showSettingsModal);
  };

  const handleSaveSettings = async (formData) => {
    if (!targetUserId) return;
    
    try {
      // Use updateUserProfile from updateProfile.js with explicit userId
      await updateUserProfile(targetUserId, formData);
      
      // Update the author state with the new data
      setAuthor(prev => ({...prev, ...formData}));
      setShowSettingsModal(false);
      
      alert("Profile updated successfully!");
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Error updating profile. Please try again.');
    }
  };

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  const handleLogoutConfirm = async () => {
    try {
      await logoutUser();
      setShowLogoutModal(false);
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      alert('Error during logout. Please try again.');
    }
  };

  const handleLogoutCancel = () => {
    setShowLogoutModal(false);
  };

  if (loading) return <div className="container">Loading...</div>;
  if (!author) return <div className="container">Author not found</div>;
  
  // التحقق من النطاق المكاني
  if (!isInRange) {
    return (
      <div className="container" style={{ padding: '3rem 1rem', textAlign: 'center' }}>
        <h2>هذا العمود غير متاح في منطقتك</h2>
        <p>يجب أن تكون ضمن نطاق 100 كم من موقع الكاتب لعرض هذا الملف الشخصي.</p>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: '#f9f7f2', minHeight: '100vh' }}>
      <div className="profile-container">
        <section className="profile-header">
          <div className="profile-actions">
            {isCurrentUser && <SettingsIcon onClick={handleSettingsToggle} />}
            {isCurrentUser && <LogoutIcon onClick={handleLogoutClick} />}
          </div>
          <SocialIcons social={author.social} />
          
          <div className="column-title-banner">
            {author.columnTitle || 'Column Title'}
          </div>
          
          <div className="profile-header-content">
            <div className="profile-image-container">
              <img 
                src={author.photoURL || '/images/default-user.png'} 
                alt={author.displayName || 'Profile'} 
                className="profile-image"
              />
            </div>
            
            <h1 className="profile-author-name">{author.displayName}</h1>
          </div>
          
          <div className="profile-bio-container">
            <p>{author.bio || ''}</p>
          </div>
        </section>

        <section className="articles-section">
          <h2 className="articles-header">artical</h2>
          
          {articles && articles.length > 0 ? (
            <ul className="article-list">
              {articles.map(article => (
                <li key={article.id} className="article-list-item">
                  <Link to={`/article/${article.id}`} className="article-link">
                    {article.title || 'Untitled Article'}
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <p style={{ textAlign: 'center', padding: '20px' }}>
              لا توجد مقالات منشورة بعد
            </p>
          )}
        </section>

        {showSettingsModal && (
          <SettingsModal 
            author={author} 
            onClose={handleSettingsToggle} 
            onSave={handleSaveSettings}
          />
        )}

        {showLogoutModal && (
          <LogoutConfirmationModal
            onClose={handleLogoutCancel}
            onConfirm={handleLogoutConfirm}
          />
        )}
      </div>
    </div>
  );
};

export default ProfilePage;
